import { PencilIcon } from '@heroicons/react/outline';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NumberFormat from 'react-number-format';
import { developerApplicationSelector } from '../../../../store';
import {
  setIsApplicationSubmitted,
  setIsLoggedIn,
} from '../../../../store/DeveloperApplication';
import { clearApplicantInfo } from '../../../../store/DeveloperApplication/ApplicantInformation';
import { clearExperience } from '../../../../store/DeveloperApplication/Experience';
import { clearLeadershipInfo } from '../../../../store/DeveloperApplication/LeadershipInformation';
import { clearProjectDetails } from '../../../../store/DeveloperApplication/ProjectDetails';
import {
  accountingFormatNumber,
  formatDate3,
  formatNumber,
  sanitizeResponseError,
} from '../../../../utils';
import Button from '../../../UI/Button';

import styles from '../../Applications.module.css';
import { boardOfDirectorsMapping } from '../LeadershipInformation/constant';
import { useMutation } from '@tanstack/react-query';
import { postPartnerProposal } from '../../../../api/Rover/partnerProposal';
import { useCurrentAccountData } from '../../../../hooks/ReactQueryHooks/useCurrentAccountQuery';

import { useNavigate } from 'react-router-dom';
import { clearProposalInformation } from '../../../../store/DeveloperApplication/ProposalInformation';

export default function Review({
  previousHandler,
  goToStepHandler,
}: {
  previousHandler: () => void;
  goToStepHandler: (step: number) => void;
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [applicationFormErrors, setApplicationFormErrors] = useState('');
  const {
    projectDetails,
    applicantInformation,
    leadershipInformation,
    experience,
  } = useSelector(developerApplicationSelector);
  const getCurrentAccountInfo = useCurrentAccountData({});
  const currentAccount = getCurrentAccountInfo?.data?.data;

  const submitApplication = useMutation(
    (payload: { formData: any; subdomain: string }) => {
      const { formData } = payload;
      const proposalInfo = {
        proposerName: applicantInformation.entityName,
        proposerEmail: applicantInformation.contactPersonEmail,
        propertyAddress: projectDetails.streetAddress,
        propertyCity: projectDetails.city,
        propertyState: projectDetails.state,
        propertyZipCode: projectDetails.zipCode,
        developmentCost: projectDetails.developmentCostEstimate,
        acquisitionType: projectDetails.acquisitionType,
        constructionType: projectDetails.constructionType,
        dealType: projectDetails.dealType,
        program: 'Lets Build Accelerator',
        responseDays: '14',
      };
      const data = {
        formData,
        ...proposalInfo,
        files: [
          ...(formData?.projectDetails?.additionalDocs || []),
          ...(formData?.leadershipInformation?.principalsResume || []),
        ],
      };
      return postPartnerProposal({ data });
    },
    {
      onSuccess: () => {
        dispatch(setIsApplicationSubmitted(true));
        navigate('/applications/submit-success', { replace: true });
        dispatch(setIsLoggedIn(false));
        dispatch(clearProposalInformation());
        dispatch(clearProjectDetails());
        dispatch(clearApplicantInfo());
        dispatch(clearLeadershipInfo());
        dispatch(clearExperience());
      },
      onError: (error: any) => {
        dispatch(setIsApplicationSubmitted(true));
        const resError = sanitizeResponseError(error);
        if (resError.includes('exist')) {
          navigate('/applications/submit-failed');
        } else {
          setApplicationFormErrors(resError);
        }
      },
    }
  );

  const submitApplicationHandler = () => {
    let applicationIsValid = true;
    applicationIsValid =
      applicationIsValid &&
      (experience.companyCompleted.length !== 0 ||
        experience.principalCompleted.length !== 0);
    applicationIsValid =
      applicationIsValid && applicantInformation.applicantInfoIsValid;
    applicationIsValid =
      applicationIsValid && leadershipInformation.leadershipInfoIsValid;

    if (applicationIsValid && currentAccount) {
      const formData = {
        projectDetails,
        applicantInformation,
        leadershipInformation,
        experience,
      };

      const data = {
        formData,
        subdomain: currentAccount?.subdomain,
      };
      submitApplication.mutate(data);
    } else {
      setApplicationFormErrors(
        'Application is invalid. Please check the form and provide all required information.'
      );
    }
  };

  return (
    <div className={`space-y-5 px-6 py-4 lg:col-span-9`}>
      <div className={styles['card']}>
        <div className={styles['card-head-section']}>
          <h3 className={styles['card-title']}>Proposed Project Details</h3>
          {/* <p className="mt-1 max-w-2xl text-sm text-gray-500">Personal details and application.</p> */}
          <div
            className='bg-gray-100 p-2 rounded-full hover:bg-blue-200 cursor-pointer'
            onClick={() => goToStepHandler(0)}
          >
            <PencilIcon className='h-5 w-5 text-blue-500' />
          </div>
        </div>
        <div className={styles['card-body-section']}>
          <dl>
            <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                Street Address
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {projectDetails.streetAddress}
              </dd>
            </div>
            <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>City</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {projectDetails.city}
              </dd>
            </div>
            <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>State</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {projectDetails.state}
              </dd>
            </div>
            <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                Zip/Postal Code
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {projectDetails.zipCode}
              </dd>
            </div>
            <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>Deal Type</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {projectDetails.dealType}
              </dd>
            </div>
            <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                Proposed Number of Units
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {formatNumber(
                  projectDetails.proposedUnits
                    ? projectDetails.proposedUnits
                    : 0
                )}
              </dd>
            </div>
            <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                Is Proposed Project "By Right"
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {projectDetails.projectByRight}
              </dd>
            </div>
            <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                Construction Type
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {projectDetails.constructionType}
              </dd>
            </div>
            <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                Acquisition Type
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {projectDetails.acquisitionType}
              </dd>
            </div>
            <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                Status of Site Control
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {projectDetails.siteControlStatus}
              </dd>
            </div>
            <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                Estimated Acquisition Cost
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {accountingFormatNumber({
                  num: projectDetails.acquisitionCostEstimate
                    ? projectDetails.acquisitionCostEstimate
                    : 0,
                  format: 'en-US',
                  currency: 'USD',
                })}
              </dd>
            </div>
            <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                Estimated Development Cost
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {accountingFormatNumber({
                  num: projectDetails.developmentCostEstimate
                    ? projectDetails.developmentCostEstimate
                    : 0,
                  format: 'en-US',
                  currency: 'USD',
                })}
              </dd>
            </div>
            <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                Estimated Pre-Development Cost
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {accountingFormatNumber({
                  num: projectDetails.preDevelopmentCostEstimate
                    ? projectDetails.preDevelopmentCostEstimate
                    : 0,
                  format: 'en-US',
                  currency: 'USD',
                })}
              </dd>
            </div>
            <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                More Information
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {projectDetails.moreInfo}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <div className={styles['card']}>
        <div className={styles['card-head-section']}>
          <h3 className={styles['card-title']}>Applicant Information</h3>
          {/* <p className="mt-1 max-w-2xl text-sm text-gray-500">Personal details and application.</p> */}
          <div
            className='bg-gray-100 p-2 rounded-full hover:bg-blue-200 cursor-pointer'
            onClick={() => goToStepHandler(1)}
          >
            <PencilIcon className='h-5 w-5 text-blue-500' />
          </div>
        </div>
        <div className={styles['card-body-section']}>
          <dl>
            <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>Entity Name</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {applicantInformation.entityName}
              </dd>
            </div>
            <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>EIN</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                <NumberFormat
                  value={applicantInformation.ein}
                  format='##-#######'
                  displayType={'text'}
                />
              </dd>
            </div>
            <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                Contact Person Name
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {applicantInformation.contactPersonName}
              </dd>
            </div>
            <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                Contact Person E-mail
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {applicantInformation.contactPersonEmail}
              </dd>
            </div>
            <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                Street Address
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {applicantInformation.streetAddress}
              </dd>
            </div>
            <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>City</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {applicantInformation.city}
              </dd>
            </div>
            <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>State</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {applicantInformation.state}
              </dd>
            </div>
            <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                Zip / Postal Code
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {applicantInformation.zipCode}
              </dd>
            </div>
            <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                Phone Number
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                <NumberFormat
                  value={applicantInformation.phone}
                  format='+1 (###) ###-####'
                  displayType={'text'}
                />
              </dd>
            </div>
            <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>Website</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {applicantInformation.website}
              </dd>
            </div>
            <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                Formation Date
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {formatDate3(
                  new Date(applicantInformation.formationDate).toDateString()
                )}
              </dd>
            </div>
            <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                Number of Full Time Employees
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {formatNumber(
                  applicantInformation.fullTimeEmployeeCount
                    ? applicantInformation.fullTimeEmployeeCount
                    : 0
                )}
              </dd>
            </div>
            <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                Following Apply
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {applicantInformation.anyApply.map((any) => (
                  <span key={any}>{any}, </span>
                ))}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <div className={styles['card']}>
        <div className={styles['card-head-section']}>
          <h3 className={styles['card-title']}>Leadership Information</h3>
          {/* <p className="mt-1 max-w-2xl text-sm text-gray-500">Personal details and application.</p> */}
          <div
            className='bg-gray-100 p-2 rounded-full hover:bg-blue-200 cursor-pointer'
            onClick={() => goToStepHandler(2)}
          >
            <PencilIcon className='h-5 w-5 text-blue-500' />
          </div>
        </div>
        <div className={styles['card-body-section']}>
          <dl>
            <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                What racial/ethnic groups does the highest authority member of
                management (President, CEO, Partner, etc) belong to?
              </dt>

              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {leadershipInformation.racialEthnicGroup.map((group) => (
                  <span key={group}>{group}, </span>
                ))}
              </dd>
            </div>
            <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                Is the majority ownership (51% or greater) of the company held
                by someone that is
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {leadershipInformation.majorityOwnership.map((major) => (
                  <span key={major}>{major}, </span>
                ))}
              </dd>
            </div>
            <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>
                Company is governed by a board of directors
              </dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                {Object.keys(leadershipInformation.boardOfDirectorsPercent).map(
                  (key) => {
                    if (
                      leadershipInformation.boardOfDirectorsPercent[
                        key
                      ].trim() !== ''
                    ) {
                      return (
                        <span key={key}>
                          {boardOfDirectorsMapping[key]}(
                          {leadershipInformation.boardOfDirectorsPercent[key]}
                          %),{' '}
                        </span>
                      );
                    }
                  }
                )}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <div className={styles['card']}>
        <div className={styles['card-head-section']}>
          <h3 className={styles['card-title']}>Experience</h3>
          {/* <p className="mt-1 max-w-2xl text-sm text-gray-500">Personal details and application.</p> */}
          <div
            className='bg-gray-100 p-2 rounded-full hover:bg-blue-200 cursor-pointer'
            onClick={() => goToStepHandler(3)}
          >
            <PencilIcon className='h-5 w-5 text-blue-500' />
          </div>
        </div>
        <div className={styles['card-body-section']}>
          <div>
            <h2 className='px-6 text-sm font-bold text-gray-500'>
              Company Completed Recent Projects
            </h2>
            {experience.companyCompleted.map((project) => (
              <dl key={project.id}>
                <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 cursor-pointer bg-green-50 text-green-500'>
                  <dt className='text-sm font-medium'>Project Name</dt>
                  <dd className='mt-1 text-sm sm:mt-0 sm:col-span-2'>
                    {project.projectName}
                  </dd>
                </div>
                <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Date Completed
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                    {formatDate3(
                      new Date(project.dateCompleted).toDateString()
                    )}
                  </dd>
                </div>
                <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Full Address
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                    {project.streetAddress}, {project.city},{' '}
                    {project.state.label}, {project.zipCode}
                  </dd>
                </div>
                <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Gross Building Area
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                    {formatNumber(
                      project.grossBuildingArea ? project.grossBuildingArea : 0
                    )}{' '}
                    Sq.Ft
                  </dd>
                </div>
                <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Number of Housing Units
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                    {formatNumber(
                      project.housingUnits ? project.housingUnits : 0
                    )}
                  </dd>
                </div>
                <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Total Development Cost
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                    {accountingFormatNumber({
                      num: project.developmentCost
                        ? project.developmentCost
                        : 0,
                      format: 'en-US',
                      currency: 'USD',
                    })}
                  </dd>
                </div>
                <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Project Type
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                    {project.projectTypes.length > 0 &&
                      project.projectTypes.map((type) => (
                        <span key={type}>{type}, </span>
                      ))}
                  </dd>
                </div>
                <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Construction Type
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                    {project.constructionTypes.length > 0 &&
                      project.constructionTypes.map((type) => (
                        <span key={type}>{type}, </span>
                      ))}
                  </dd>
                </div>
                <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Company's Role
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                    {project.companyRole &&
                      project.companyRole.map((role) => (
                        <span key={role}>{role}, </span>
                      ))}
                    {project.partialOwner && (
                      <span>Partial Owner ({project.partialOwner}%)</span>
                    )}
                  </dd>
                </div>
              </dl>
            ))}
            {experience.companyCompleted.length === 0 && (
              <div className='px-6'>None</div>
            )}
          </div>
          <div>
            <h2 className='px-6 text-sm font-bold text-gray-500'>
              Principal Completed Recent Projects
            </h2>
            {experience.principalCompleted.map((project) => (
              <dl key={project.id}>
                <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Company Name
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                    {project.companyName}
                  </dd>
                </div>
                <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Project Name
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                    {project.projectName}
                  </dd>
                </div>
                <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Date Completed
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                    {formatDate3(
                      new Date(project.dateCompleted).toDateString()
                    )}
                  </dd>
                </div>
                <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Full Address
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                    {project.streetAddress}, {project.city},{' '}
                    {project.state.label}, {project.zipCode}
                  </dd>
                </div>
                <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Gross Building Area
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                    {formatNumber(
                      project.grossBuildingArea ? project.grossBuildingArea : 0
                    )}{' '}
                    Sq.Ft
                  </dd>
                </div>
                <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Number of Housing Units
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                    {formatNumber(
                      project.housingUnits ? project.housingUnits : 0
                    )}
                  </dd>
                </div>
                <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Development Cost
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                    {accountingFormatNumber({
                      num: project.developmentCost
                        ? project.developmentCost
                        : 0,
                      format: 'en-US',
                      currency: 'USD',
                    })}
                  </dd>
                </div>
                <div className='bg-white px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Project Types
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                    {project.projectTypes.length > 0 &&
                      project.projectTypes.map((type) => (
                        <span key={type}>{type}, </span>
                      ))}
                  </dd>
                </div>
                <div className='bg-gray-50 px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Construction Type
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                    {project.constructionTypes.length > 0 &&
                      project.constructionTypes.map((type) => (
                        <span key={type}>{type}, </span>
                      ))}
                  </dd>
                </div>
              </dl>
            ))}
            {experience.principalCompleted.length === 0 && (
              <div className='px-6'>None</div>
            )}
          </div>
        </div>
      </div>
      <p className='text-tertiaryColor-normal'>{applicationFormErrors}</p>
      <div className='flex gap-4 justify-end'>
        <Button
          label='Previous'
          btnStyle='btn-neutral'
          type='button'
          disabled={submitApplication.isLoading}
          onClick={() => previousHandler()}
        />
        <Button
          label={`${submitApplication.isLoading ? 'Submitting...' : 'Submit'}`}
          btnStyle='btn-primary'
          type='button'
          disabled={submitApplication.isLoading}
          onClick={submitApplicationHandler}
        />
      </div>
    </div>
  );
}
